/**
 * 돌봄 계산기 감경 문항 답 → 감경 유형(ReductionType) 변환.
 *
 * 영구지침: docs/develop/carecalc/README.md §3-4, §3-4-1
 *
 * `POST /estimate` 응답에는 적용된 감경 유형이 없어서, 결과 화면의 시설 목록
 * (`GET /care-calculator/facilities`)에 넘길 값을 프론트가 문항 답에서 구한다.
 * **백엔드 `CareCalculatorRuleset.toReductionType` 과 반드시 같아야 한다.**
 * 한쪽을 바꾸면 다른 쪽과 reduction.test.ts 를 같이 바꾼다.
 */

/** 감경 문항 코드 */
export const REDUCTION_QUESTION_CODE = 'Q_REDUCTION'

/** 백엔드 `ReductionType` enum (시설 조회 파라미터) */
export type CareCalcReductionType =
  | 'NONE' // 일반 — 시설 20% / 재가 15%
  | 'BASIC_LIVELIHOOD' // 면제 — 0% / 0%
  | 'NEAR_POVERTY_1' // 60% 감경 — 8% / 6% (enum 이름은 백엔드 계약이라 유지)
  | 'NEAR_POVERTY_2' // 40% 감경 — 12% / 9% (enum 이름은 백엔드 계약이라 유지)

/**
 * 감경 문항 선택지 값 → 감경 유형.
 *
 * 선택지 값은 ReductionType 이름이 아니다. 같은 유형에 여러 선택지가 걸린다.
 * 옛 v2 값(`NEAR_POVERTY_1`·`NEAR_POVERTY_2`)은 뜻이 바뀌었으므로 서로 뒤집혀 매핑된다.
 */
export function toReductionType(answer: string | null | undefined): CareCalcReductionType {
  switch (answer) {
    case 'BASIC_LIVELIHOOD':
      return 'BASIC_LIVELIHOOD'
    case 'MEDICAL_AID_OTHER':
    case 'NEAR_POVERTY_RELIEF':
    case 'PREMIUM_LOW_25':
      return 'NEAR_POVERTY_1'
    case 'PREMIUM_LOW_50':
      return 'NEAR_POVERTY_2'
    // v2 호환 — 옛 "의료급여를 받으세요" → 60%, 옛 "건강보험료를 적게 내시는 편" → 40%
    case 'NEAR_POVERTY_2':
      return 'NEAR_POVERTY_1'
    case 'NEAR_POVERTY_1':
      return 'NEAR_POVERTY_2'
    // UNKNOWN · NONE · 미응답 · 알 수 없는 값 → 보수적으로 일반
    default:
      return 'NONE'
  }
}

/** 계산기 답변 전체에서 감경 유형을 구한다 */
export function reductionTypeFromAnswers(
  answers: Record<string, string>,
): CareCalcReductionType {
  return toReductionType(answers[REDUCTION_QUESTION_CODE])
}
